import { useState } from 'react';
import EditableSection from '../../components/EditableSection';
import { useEditableDraft } from '../../hooks/useEditableDraft';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useUpdateDesiredPaymentProviderMutation } from '../../api/account/accountApiSlice';
import {
    TenantDesiredPaymentProvider,
    TenantPaymentIntegration,
} from '../../api/account/accountApiTypes';
import { enqueueSuccessSnackbar } from '../../action/snackbarCommands';

type PaymentProviderSectionProps = {
    readOnly: boolean;
    /** Derived Stripe status of the tenant IN VIEW. Passed in rather than
     *  re-queried so the block can never report a different tenant than the
     *  page around it (#1334). */
    integration: TenantPaymentIntegration | undefined;
    /** The provider the tenant WANTS — stored wish, passed in (#1334). */
    desiredPaymentProvider: TenantDesiredPaymentProvider | null;
    /** Master path: write the VIEWED tenant by slug. Absent = caller's own. */
    masterSlug?: string;
};

/**
 * The payment provider — the tenant CHOOSES which provider they want (PO,
 * 2026-08-19, Neuland staging review); connecting stays a manual step kolula
 * performs. The choice is a stored wish, never a status: the connection is
 * derived live from the credentials the booking flow itself reads
 * (`payment-integration`), so a wish can never claim a connection the flow
 * does not have — the `shared_credentials` middle state (#997) stays visible.
 *
 * Three states per provider: wished + connected (all good), wished + not
 * connected (guidance: contact kolula, we set it up together), not wished
 * (plain option). A connected provider renders checked and locked — the
 * connection is the stronger fact, and un-wishing it would not disconnect
 * anything.
 *
 * It sits in Account next to the bank details because both are credentials to
 * deposit (ADR-0010): whoever configures offers in Services should not thereby
 * see bank details. The Services area links here when a choice it offers needs
 * a connection that is missing.
 */
const PaymentProviderSection = ({
    readOnly,
    integration,
    desiredPaymentProvider,
    masterSlug,
}: PaymentProviderSectionProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [updateProvider, { isLoading }] = useUpdateDesiredPaymentProviderMutation();
    const [error, setError] = useState<string | null>(null);

    const connected = integration?.stripe === 'connected';
    // The wish is one editable unit (design-decisions › Bearbeiten und
    // Speichern; PO 2026-10-12): it no longer saves on every click.
    const draft = useEditableDraft<boolean>(desiredPaymentProvider === 'stripe');
    const stripeChecked = connected || draft.value;

    // Which explanation fits the state — connected beats wished beats plain.
    let hintKey = 'account.payments_integration_setup_hint';
    if (connected) hintKey = 'account.payments_integration_connected_hint';
    else if (stripeChecked) hintKey = 'account.payment_provider_guidance';

    const save = async () => {
        setError(null);
        try {
            await updateProvider({ desiredPaymentProvider: draft.value ? 'stripe' : null, slug: masterSlug }).unwrap();
            dispatch(enqueueSuccessSnackbar({ message: t('account.saved'), autoHideDuration: 3000 }));
            draft.finish();
        } catch (e) {
            const message = (e as { data?: { message?: string } })?.data?.message;
            setError(message ?? t('account.error_generic'));
        }
    };

    const renderFields = (locked: boolean) => (
        <div>
            <label className={'flex items-start gap-2 text-sm text-foreground'}>
                <input
                    type={'checkbox'}
                    className={'mt-1'}
                    checked={stripeChecked}
                    disabled={locked || isLoading || connected}
                    onChange={(event) => draft.setDraft(event.target.checked)}
                />
                <span>
                    {t('account.payment_provider_stripe')}
                    <span className={'block text-xs text-foreground/60'}>
                        {t('account.payment_provider_stripe_hint')}
                    </span>
                </span>
            </label>

            <div className={'mt-4'}>
                <p className={'text-sm text-foreground'}>
                    {integration
                        ? t(`account.payments_integration_${integration.stripe}`)
                        : t('common.not_applicable')}
                </p>
                {/* WHICH account the key belongs to — read live from Stripe
                    (PO, 2026-08-19: "exists" is not evidence). Absent when the
                    read failed; the derived status above still stands. */}
                {integration?.account && (
                    <p className={'text-sm text-foreground mt-1'}>
                        {t('account.payment_provider_account', {
                            name: integration.account.name
                                ?? t('common.not_applicable'),
                            id: integration.account.maskedId,
                        })}
                    </p>
                )}
                {connected && integration?.connectAccount && (
                    <p className={'text-xs text-foreground/60 mt-1'}>
                        {t('account.payment_provider_connect_note')}
                    </p>
                )}
                <p className={'text-xs text-foreground/60 mt-1'}>
                    {t(hintKey)}
                </p>
            </div>
        </div>
    );

    return (
        <EditableSection
            id={'payment-provider'}
            title={t('account.payment_provider_section')}
            description={t('account.payment_provider_section_desc')}
            // Nothing to wish for once Stripe is connected.
            readOnly={readOnly || connected}
            editing={draft.editing}
            isDirty={draft.isDirty}
            saving={isLoading}
            error={error}
            onEdit={draft.startEdit}
            onCancel={() => { setError(null); draft.cancel(); }}
            onSave={() => { void save(); }}
            view={renderFields(true)}
        >
            {renderFields(false)}
        </EditableSection>
    );
};

export default PaymentProviderSection;
